/**
 * @file components/CardListing/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 * @author Fitore Jaha [fitore.jaha@cbc.ca]
 */

import { useEffect, useRef } from "react";
import { string, number, bool, object, func, oneOf } from "prop-types";
import CardImageWrap from "../CardImageWrap/component";
import Headline from "../Headline/component";
import Metadata from "../Metadata/component";
import { Label } from "../../../Label/component";
import Description from "../../../Description/component";
import LinkWrap from "../../../LinkWrap/component";
import classNames from "classnames";
import { createLinkProps } from "../../../../utils/dataTransform/utils";
import {
    DEFAULT_IMAGE_POSITION,
    IMAGE_POSITIONS,
    IMAGE_SIZE_SLOTS,
} from "./constants";
import { useNormalizeClass } from "../../../../hooks/useNormalizeClass";
import { useImageDerivatives } from "../../../../hooks/useImageDerivatives";
import { usePreviewBase } from "../../../../hooks/usePreviewBase";
import { convertImageDerivativesIntoSrcSet } from "../../../../utils/dataTransform/utils";

/**
 * Function representing the CardListing component.
 *
 * This card type appears mainly on the Show pages
 *
 */
const CardListing = ({
    external,
    title,
    url,
    className,
    description = "",
    feature = {},
    flag = "none",
    flagId,
    imageURL,
    image,
    imagePosition = DEFAULT_IMAGE_POSITION,
    author = {
        name: "",
        image: "",
    },
    displayComments,
    baseSection,
    subSection,
    updateTime,
    departments,
    headlineId,
    descriptionId,
    metadataId,
    sourceId,
    cardClickHandler,
    focus,
    videoCardOverlayType,
    videoDuration,
    videoAirDate,
    itemType,
    showTimestamp = true,
    displayAuthor,
    hideAuthorInfo,
    videoStreamType,
}) => {
    const domNode = useRef(null);
    /**
     * Fires after component renders. Handle any post-rendering logic
     *
     * @returns {void}
     */
    useEffect(() => {
        // assign focus based on props
        if (focus) {
            domNode.current.focus();
        }
    }, [focus]);

    // This card has three states for the image position (top, left, right)
    const imagePositionClass = `${imagePosition}Image`;
    const flagClassName = useNormalizeClass("flag", flag);

    // Apply all possible classnames
    const classes = classNames(
        "card",
        flagClassName,
        "cardListing",
        imagePositionClass,
        className
    );

    let metaProps = {
        baseSection: baseSection,
        subSection: subSection,
        updated: updateTime,
        showTimestamp: showTimestamp,
        departments: departments,
        url: url,
        displayComments: displayComments,
        hideAuthorInfo,
    };

    if (displayAuthor && author) {
        const { authorName, authorImage } = {
            authorName: author?.name,
            authorImage: author?.image,
        };

        metaProps = { authorName, authorImage, ...metaProps };
    }

    const labelProps = {
        flag: flag,
        external: external,
    };

    imageURL = usePreviewBase(imageURL);
    const [srcsetAttr] = useImageDerivatives(imageURL);

    let imageSrcSet = srcsetAttr;
    if (imageSrcSet.length === 0) {
        imageSrcSet = convertImageDerivativesIntoSrcSet(image);
        imageSrcSet =
            imageSrcSet?.filter((item) => !item.includes("220w")) || [];
    }

    const imageProps = {
        url: imageURL,
        srcSet: imageSrcSet,
        sizes: IMAGE_SIZE_SLOTS,
    };

    const videoLinkImageOverlayProps = {
        videoCardOverlayType,
        videoDuration,
        videoAirDate,
        videoStreamType,
    };
    return (
        <LinkWrap
            className={classes}
            {...createLinkProps({
                sourceId,
                external,
                url,
                cardClickHandler,
            })}
            data-cy={`type-${itemType}`}
            data-testid="card-listing"
            {...feature}
            ref={domNode}
        >
            <CardImageWrap
                {...{ imageProps, videoLinkImageOverlayProps }}
                className={classNames("image", "imageMedia")}
            />
            <div className="contentWrapper">
                <div className="card-content">
                    <div className="card-content-top">
                        <Label id={flagId} {...labelProps} />
                        <Headline id={headlineId} text={title} />
                        <Description id={descriptionId} text={description} />
                    </div>
                    <div className="card-content-bottom">
                        <Metadata id={metadataId} {...metaProps} />
                    </div>
                </div>
            </div>
        </LinkWrap>
    );
};

CardListing.propTypes = {
    // REQUIRED
    // Is the URL external (in which case we don't handle it)?
    external: bool.isRequired,
    // The headline to display.
    title: string.isRequired,
    // The URL that the card links to
    url: string.isRequired,
    // OPTIONAL
    className: string,
    // The extended description of the story
    description: string,
    // The flag for the card
    flag: string,
    // The flag for the card
    imagePosition: oneOf(IMAGE_POSITIONS),
    // Author name and image object
    author: object,
    // Display comments for this card
    displayComments: bool,

    focus: bool,

    flagId: string,

    imageURL: string,

    baseSection: string,

    subSection: string,

    updateTime: number,

    departments: object,

    headlineId: string,

    descriptionId: string,

    metadataId: string,

    onClick: func,

    cardClickHandler: func,

    itemType: string,

    showTimestamp: bool,
};

export default CardListing;
